/**
 * Utility Functions
 * Common helper functions used throughout the application
 *
 */

/**
 * Simple event bus for component communication
 */
class EventBus {
  constructor() {
    this.events = {};
  }

  on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  }

  emit(event, data) {
    if (this.events[event]) {
      this.events[event].forEach(callback => callback(data));
    }
  }

  off(event, callback) {
    if (this.events[event]) {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    }
  }
}

// Export singleton instance for app-wide use
export const eventBus = new EventBus();

/**
 * Determine whether source content is newer than its last viewed timestamp.
 * Missing or invalid source dates are not treated as new once a viewed stamp
 * exists; a missing or invalid viewed date keeps valid source content new.
 *
 * @param {string|Date|null|undefined} sourceDate
 * @param {string|Date|null|undefined} viewedDate
 * @returns {boolean}
 */
export function isNewSinceViewed(sourceDate, viewedDate) {
  if (!sourceDate) return !viewedDate;
  if (!viewedDate) return true;

  const source = sourceDate instanceof Date ? sourceDate : new Date(sourceDate);
  const viewed = viewedDate instanceof Date ? viewedDate : new Date(viewedDate);
  if (Number.isNaN(source.getTime())) return false;
  if (Number.isNaN(viewed.getTime())) return true;
  return source.getTime() > viewed.getTime();
}

/**
 * Escape HTML special characters for safe text or attribute interpolation.
 * @param {string} str - String to escape
 * @returns {string} Escaped string safe for HTML insertion
 */
export function escapeHtml(str) {
  if (!str) return '';
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return String(str).replace(/[&<>"']/g, char => map[char]);
}

/**
 * Check if a name is a guest-style username that shouldn't be displayed
 * Guest usernames look like "guest_7f6b96d8" or "guest_guest_7f6b96d8"
 * @param {string} name - The name to check
 * @returns {boolean} True if the name is a guest-style name
 */
export function isGuestName(name) {
  if (!name || typeof name !== 'string') return false;
  return name.toLowerCase().startsWith('guest_');
}
